import { readNullableString, readRecord } from '@/lib/readers';

const LIGHTNESS: Record<ScaleStep, number> = {
  50: 97,
  100: 93,
  200: 86,
  300: 76,
  400: 64,
  500: 54,
  600: 44,
  700: 36,
  800: 28,
  900: 22,
};

const SATURATION_DAMPING: Partial<Record<ScaleStep, number>> = {
  50: 0.5,
  100: 0.65,
  200: 0.8,
  300: 0.9,
};

export const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
export type ScaleStep = (typeof SCALE_STEPS)[number];

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsl {
  hue: number;
  saturation: number;
  lightness: number;
}

/** Returns the channels of a `#rgb` or `#rrggbb` colour, or null when it is not one. */
export function parseHex(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }: Rgb): [number, number, number] {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) return [0, 0, lightness * 100];

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue: number;
  if (max === red) hue = (green - blue) / delta + (green < blue ? 6 : 0);
  else if (max === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;

  return [hue * 60, saturation * 100, lightness * 100];
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const saturation = s / 100;
  const lightness = l / 100;
  const a = saturation * Math.min(lightness, 1 - lightness);

  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round(
      255 * (lightness - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))),
    );
  };

  return { r: channel(0), g: channel(8), b: channel(4) };
}

/** Returns the hue, saturation and lightness of a hex colour, or null when it is not one. */
export function hexToHsl(hex: string): Hsl | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;

  const [hue, saturation, lightness] = rgbToHsl(rgb);
  return { hue, saturation, lightness };
}

/** Returns one HSL colour as space-separated RGB channels. */
export function hslToChannels(hue: number, saturation: number, lightness: number): string {
  const { r, g, b } = hslToRgb(hue, saturation, lightness);
  return `${r} ${g} ${b}`;
}

/** Returns the brand scale of one hex colour, or null when it is not one. Step 600 is the colour. */
export function generateScale(hex: string): Record<ScaleStep, string> | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;

  const [hue, saturation] = rgbToHsl(rgb);

  const scale = {} as Record<ScaleStep, string>;
  for (const step of SCALE_STEPS) {
    scale[step] =
      step === 600
        ? `${rgb.r} ${rgb.g} ${rgb.b}`
        : hslToChannels(hue, saturation * (SATURATION_DAMPING[step] ?? 1), LIGHTNESS[step]);
  }
  return scale;
}

/** Returns space-separated RGB channels as a CSS colour. */
export function toCssColor(channels: string): string {
  return `rgb(${channels})`;
}

// The neutral ramp: the saturation and lightness of the hand-tuned scale in
// tokens.css, on the hue of the organization's primary colour.
interface Tone {
  saturation: number;
  lightness: number;
}

/** The saturation of the default primary, the hue these tones were tuned for. */
const REFERENCE_SATURATION = 98;

export const NEUTRAL_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 850, 900, 950] as const;
export type NeutralStep = (typeof NEUTRAL_STEPS)[number];

const LIGHT_TONES: Record<NeutralStep, Tone> = {
  50: { saturation: 46.7, lightness: 97.1 },
  100: { saturation: 45.5, lightness: 97.8 },
  200: { saturation: 38.8, lightness: 90.4 },
  300: { saturation: 29.0, lightness: 81.8 },
  400: { saturation: 25.8, lightness: 65.1 },
  500: { saturation: 17.7, lightness: 47.6 },
  600: { saturation: 19.0, lightness: 41.2 },
  700: { saturation: 17.3, lightness: 35.1 },
  800: { saturation: 16.9, lightness: 30.2 },
  850: { saturation: 42.9, lightness: 26.1 },
  900: { saturation: 81.8, lightness: 21.6 },
  950: { saturation: 79.3, lightness: 11.4 },
};

const DARK_TONES: Record<NeutralStep, Tone> = {
  50: { saturation: 48.8, lightness: 8.4 },
  100: { saturation: 43.8, lightness: 12.5 },
  200: { saturation: 35.2, lightness: 21.2 },
  300: { saturation: 31.9, lightness: 28.2 },
  400: { saturation: 17.7, lightness: 47.6 },
  500: { saturation: 25.8, lightness: 65.1 },
  600: { saturation: 26.1, lightness: 72.9 },
  700: { saturation: 26.3, lightness: 77.6 },
  800: { saturation: 29.0, lightness: 81.8 },
  850: { saturation: 32.1, lightness: 89.0 },
  900: { saturation: 41.7, lightness: 95.3 },
  950: { saturation: 45.5, lightness: 97.8 },
};

/** Returns the neutral scale for one theme. The saturation of the primary colour
 *  decreases the tint. Returns null if the colour is not valid. */
export function generateNeutralScale(
  hex: string,
  dark: boolean,
): Record<NeutralStep, string> | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;

  const damping = Math.min(hsl.saturation / REFERENCE_SATURATION, 1);
  const tones = dark ? DARK_TONES : LIGHT_TONES;

  const scale = {} as Record<NeutralStep, string>;
  for (const step of NEUTRAL_STEPS) {
    scale[step] = hslToChannels(hsl.hue, tones[step].saturation * damping, tones[step].lightness);
  }
  return scale;
}

// The one module that sets the brand custom properties on the document root.
// A caller adds or removes a layer, and the root gets the values of the top layer.
const STORAGE_KEY = 'edify_brand_colors';

export interface BrandColors {
  primary?: string;
  secondary?: string;
}

let base: BrandColors = {};
let preview: BrandColors | null = null;
let themeWatched = false;

function writeScale(
  prefix: string,
  steps: readonly number[],
  scale: Record<number, string> | null,
): void {
  const { style } = document.documentElement;
  for (const step of steps) {
    const property = `--color-${prefix}-${step}`;
    const value = scale?.[step];
    if (value) style.setProperty(property, value);
    else style.removeProperty(property);
  }
}

function paint(): void {
  const { primary, secondary } = preview ?? base;
  const dark = document.documentElement.classList.contains('dark');

  writeScale('brand', SCALE_STEPS, primary ? generateScale(primary) : null);
  writeScale('accent', SCALE_STEPS, secondary ? generateScale(secondary) : null);
  writeScale('neutral', NEUTRAL_STEPS, primary ? generateNeutralScale(primary, dark) : null);
}

/** Sets the root from the top layer each time the theme changes. */
function watchTheme(): void {
  if (themeWatched) return;
  themeWatched = true;
  new MutationObserver(paint).observe(document.documentElement, { attributeFilter: ['class'] });
}

function persist(colors: BrandColors): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch {
    // Ignores a full or blocked store.
  }
}

/** Applies the saved colours and persists them. An update with neither colour is ignored. */
export function setBaseBrandColors(colors: BrandColors): void {
  if (!colors.primary && !colors.secondary) return;
  base = colors;
  persist(colors);
  watchTheme();
  paint();
}

/** Puts the colours in edit above the saved colours. Null removes them again. */
export function setPreviewBrandColors(colors: BrandColors | null): void {
  preview = colors;
  watchTheme();
  paint();
}

/** Sets the colours saved in the last session, before the app gets the organization. */
export function restoreBrandColors(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored = readRecord(JSON.parse(raw));
    setBaseBrandColors({
      primary: readNullableString(stored.primary) ?? undefined,
      secondary: readNullableString(stored.secondary) ?? undefined,
    });
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/** Removes both layers and the stored palette, then sets the root again. */
export function clearBrandColors(): void {
  base = {};
  preview = null;
  localStorage.removeItem(STORAGE_KEY);
  paint();
}
